// src/services/tenantDataService.js
import { dataService } from './dataService.js';

/**
 * Tenant-aware data service that automatically scopes data by tenant ID
 * This ensures complete data isolation between different clinics
 */
class TenantDataService {
  constructor() {
    this.currentTenantId = null;
  }

  /**
   * Set the current tenant context
   * All subsequent operations will be scoped to this tenant
   */
  setTenantContext(tenantId) {
    if (!tenantId) {
      console.error('❌ TenantDataService: Cannot set empty tenant context');
      throw new Error('Tenant ID is required');
    }

    this.currentTenantId = tenantId;
    console.log(`🏥 TenantDataService: Switched to tenant "${tenantId}"`);
  }

  /**
   * Get the current tenant ID
   */
  getCurrentTenantId() {
    return this.currentTenantId;
  }

  /**
   * Get tenant-scoped collection path for Firestore subcollections
   */
  _getTenantScopedCollection(collectionName) {
    if (!collectionName) {
      throw new Error('Collection name is required');
    }

    if (!this.currentTenantId) {
      console.error(`❌ TenantDataService: No tenant context set for collection "${collectionName}"`);
      throw new Error('Tenant context must be set before accessing data. Call setTenantContext() first.');
    }

    // Use Firestore subcollection structure: tenants/{tenantId}/{collection}
    const scopedPath = `tenants/${this.currentTenantId}/${collectionName}`;
    console.log(`🔍 TenantDataService: Scoped "${collectionName}" → "${scopedPath}"`);
    return scopedPath;
  }

  /**
   * Get all items from a tenant-scoped collection
   */
  async getAll(collectionName) {
    try {
      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.getAll(scopedCollection);
      console.log(`📊 TenantDataService: Retrieved ${result.length} items from "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.getAll(${collectionName}) failed:`, error);
      throw new Error(`Failed to retrieve ${collectionName} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Get item by ID from a tenant-scoped collection
   */
  async getById(collectionName, id) {
    try {
      if (!id) {
        throw new Error('Document ID is required');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.getById(scopedCollection, id);
      console.log(`🔍 TenantDataService: Retrieved item ${id} from "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.getById(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to retrieve ${collectionName}/${id} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Create item in a tenant-scoped collection
   */
  async create(collectionName, data) {
    try {
      if (!data || typeof data !== 'object') {
        throw new Error('Valid data object is required');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.create(scopedCollection, data);
      console.log(`✅ TenantDataService: Created item ${result.id} in "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.create(${collectionName}) failed:`, error);
      throw new Error(`Failed to create ${collectionName} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Update item in a tenant-scoped collection
   */
  async update(collectionName, id, data) {
    try {
      if (!id) {
        throw new Error('Document ID is required');
      }
      if (!data || typeof data !== 'object') {
        throw new Error('Valid data object is required');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.update(scopedCollection, id, data);
      console.log(`📝 TenantDataService: Updated item ${id} in "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.update(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to update ${collectionName}/${id} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Delete item from a tenant-scoped collection
   */
  async delete(collectionName, id) {
    try {
      if (!id) {
        throw new Error('Document ID is required');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.delete(scopedCollection, id);
      console.log(`🗑️ TenantDataService: Deleted item ${id} from "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.delete(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to delete ${collectionName}/${id} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Batch delete items from a tenant-scoped collection
   */
  async batchDelete(collectionName, ids) {
    try {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        throw new Error('Document IDs array is required');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.batchDelete(scopedCollection, ids);
      console.log(`🗑️ TenantDataService: Batch deleted ${result} items from "${scopedCollection}"`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.batchDelete(${collectionName}) failed:`, error);
      throw new Error(`Failed to batch delete ${collectionName} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Query items from a tenant-scoped collection
   */
  async query(collectionName, conditions = []) {
    try {
      if (!Array.isArray(conditions)) {
        throw new Error('Query conditions must be an array');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      const result = await dataService.query(scopedCollection, conditions);
      console.log(`🔍 TenantDataService: Queried ${result.length} items from "${scopedCollection}" with ${conditions.length} conditions`);
      return result;
    } catch (error) {
      console.error(`❌ TenantDataService.query(${collectionName}) failed:`, error);
      throw new Error(`Failed to query ${collectionName} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Subscribe to real-time updates from a tenant-scoped collection
   */
  subscribe(collectionName, callback, conditions = []) {
    try {
      if (typeof callback !== 'function') {
        throw new Error('Callback function is required');
      }
      if (!Array.isArray(conditions)) {
        throw new Error('Query conditions must be an array');
      }

      const scopedCollection = this._getTenantScopedCollection(collectionName);
      console.log(`🔔 TenantDataService: Subscribing to "${scopedCollection}" with ${conditions.length} conditions`);
      return dataService.subscribe(scopedCollection, callback, conditions);
    } catch (error) {
      console.error(`❌ TenantDataService.subscribe(${collectionName}) failed:`, error);
      throw new Error(`Failed to subscribe to ${collectionName} for tenant ${this.currentTenantId}: ${error.message}`);
    }
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(listenerId) {
    return dataService.unsubscribe(listenerId);
  }

  /**
   * Get current tenant context
   */
  getCurrentTenantId() {
    return this.currentTenantId;
  }

  /**
   * Check if tenant context is set
   */
  hasTenantContext() {
    return this.currentTenantId !== null;
  }

  /**
   * Migrate data from non-scoped to tenant-scoped collections
   * This is useful for migrating existing data to multi-tenant structure
   */
  async migrateDataToTenant(collectionName, tenantId) {
    try {
      console.log(`🔄 TenantDataService: Migrating "${collectionName}" to tenant "${tenantId}"`);

      // Get data from original collection
      const originalData = await dataService.getAll(collectionName);

      if (originalData.length === 0) {
        console.log(`ℹ️ TenantDataService: No data to migrate for "${collectionName}"`);
        return { migrated: 0, skipped: 0 };
      }

      // Save to tenant-scoped collection
      const tenantScopedCollection = `${collectionName}_${tenantId}`;

      // Check if tenant collection already has data
      const existingTenantData = await dataService.getAll(tenantScopedCollection);
      if (existingTenantData.length > 0) {
        console.log(`⚠️ TenantDataService: Tenant collection "${tenantScopedCollection}" already has data, skipping migration`);
        return { migrated: 0, skipped: originalData.length };
      }

      // Migrate each item
      let migratedCount = 0;
      for (const item of originalData) {
        await dataService.create(tenantScopedCollection, item);
        migratedCount++;
      }

      console.log(`✅ TenantDataService: Migrated ${migratedCount} items from "${collectionName}" to "${tenantScopedCollection}"`);
      return { migrated: migratedCount, skipped: 0 };

    } catch (error) {
      console.error(`❌ TenantDataService: Error migrating "${collectionName}" to tenant "${tenantId}":`, error);
      throw error;
    }
  }

  /**
   * Copy data from one tenant to another
   * Useful for setting up new tenants with template data
   */
  async copyDataBetweenTenants(collectionName, sourceTenantId, targetTenantId) {
    try {
      console.log(`🔄 TenantDataService: Copying "${collectionName}" from "${sourceTenantId}" to "${targetTenantId}"`);

      const sourceCollection = `${collectionName}_${sourceTenantId}`;
      const targetCollection = `${collectionName}_${targetTenantId}`;

      const sourceData = await dataService.getAll(sourceCollection);

      if (sourceData.length === 0) {
        console.log(`ℹ️ TenantDataService: No data to copy for "${collectionName}"`);
        return 0;
      }

      let copiedCount = 0;
      for (const item of sourceData) {
        // Remove ID to create new items
        const { id, ...itemData } = item;
        await dataService.create(targetCollection, itemData);
        copiedCount++;
      }

      console.log(`✅ TenantDataService: Copied ${copiedCount} items from "${sourceTenantId}" to "${targetTenantId}"`);
      return copiedCount;

    } catch (error) {
      console.error(`❌ TenantDataService: Error copying data between tenants:`, error);
      throw error;
    }
  }
}

// Export singleton instance
const tenantDataService = new TenantDataService();

// Export getCurrentTenantId as a standalone function
export function getCurrentTenantId() {
  return tenantDataService.getCurrentTenantId();
}

export { tenantDataService };
export default tenantDataService;
